import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Home, ChevronRight, DollarSign, ClipboardPlus, Loader2, Plus, Check, ChevronsUpDown, Search, FileSpreadsheet } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from 'sonner';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateWorksOrder = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [parts, setParts] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);
  const [formData, setFormData] = useState({
    order_number: '',
    customer: '',
    customer_order_number: '',
    part_number: '',
    quantity: '',
    unit_price: '',
    due_date: '',
    notes: '',
    is_trial: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [barsNeeded, setBarsNeeded] = useState(0);
  const [partSearchOpen, setPartSearchOpen] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  
  // Quick add customer dialog
  const [showAddCustomer, setShowAddCustomer] = useState(false);
  const [newCustomerName, setNewCustomerName] = useState('');
  const [addingCustomer, setAddingCustomer] = useState(false);

  useEffect(() => {
    fetchParts();
    fetchCustomers();
  }, []);

  const fetchParts = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/parts`);
      // Sort parts alphabetically by part_number
      const sortedParts = response.data.sort((a, b) => 
        a.part_number.localeCompare(b.part_number)
      );
      setParts(sortedParts);
    } catch (error) {
      console.error('Error fetching parts:', error);
      toast.error('Failed to load parts');
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customers`);
      // Only show active customers
      setCustomers(response.data.filter(c => c.is_active !== false));
    } catch (error) {
      console.error('Error fetching customers:', error);
    }
  };

  const handleQuickAddCustomer = async () => {
    if (!newCustomerName.trim()) {
      toast.error('Please enter a customer name');
      return;
    }
    
    setAddingCustomer(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/customers`, {
        name: newCustomerName.trim()
      });
      toast.success('Customer added');
      setCustomers(prev => [...prev, response.data]);
      setFormData(prev => ({ ...prev, customer: response.data.name }));
      setShowAddCustomer(false);
      setNewCustomerName('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add customer');
    } finally {
      setAddingCustomer(false);
    }
  };

  const handlePartSelect = (partNumber) => {
    const part = parts.find(p => p.part_number === partNumber);
    setSelectedPart(part);
    setFormData(prev => ({
      ...prev,
      part_number: partNumber
    }));
    setBarsNeeded(0);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    if (field === 'quantity' && selectedPart && value) {
      const quantity = parseInt(value);
      const piecesPerBar = Math.floor(5800 / (selectedPart.loc + 2));
      const barsRequired = Math.ceil(quantity / piecesPerBar);
      setBarsNeeded(barsRequired);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.part_number || !formData.customer || !formData.quantity || !formData.due_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      const orderData = {
        ...formData,
        quantity: parseInt(formData.quantity),
        unit_price: parseFloat(formData.unit_price) || 0,
        is_trial: formData.is_trial
      };
      if (!formData.order_number.trim()) {
        delete orderData.order_number;
      }
      const response = await axios.post(`${BACKEND_URL}/api/works-orders`, orderData);
      toast.success(`Works Order ${response.data.order_number} created successfully`);
      setCreatedOrder(response.data);
    } catch (error) {
      console.error('Error creating works order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create works order');
    } finally {
      setSubmitting(false);
    }
  };

  const generateTraveller = () => {
    if (!createdOrder) return;

    const workbook = XLSX.utils.book_new();
    const order = createdOrder;
    const part = selectedPart || {};

    // Build the traveller sheet
    const rows = [];

    // Header Section
    rows.push(['Hotforge (Pty) Ltd']);
    rows.push(['Works Order Traveller / Routing Sheet']);
    rows.push(['']);
    rows.push(['Works Order Number:', '', order.order_number]);
    rows.push(['Due Date:', '', order.due_date || '']);
    rows.push(['']);

    // Order Information Section (A=label, C=value)
    rows.push(['ORDER DETAILS']);
    rows.push(['Field', '', 'Value']);
    rows.push(['Works Order Number', '', order.order_number]);
    rows.push(['Customer Name', '', order.customer || '']);
    rows.push(['Customer Order Number', '', order.customer_order_number || '']);
    rows.push(['Part Description', '', part.description || order.part_number || '']);
    rows.push(['Part Number', '', order.part_number || '']);
    rows.push(['Drawing Number', '', part.drawing_number || '']);
    rows.push(['Material', '', part.material_grade || '']);
    rows.push(['LOC / Length of Cut', '', part.loc ? `${part.loc} mm` : '']);
    rows.push(['Diameter', '', part.diameter ? `${part.diameter} mm` : '']);
    rows.push(['Quantity', '', order.quantity || '']);
    rows.push(['Heat Treatment Specification', '', part.heat_treatment || '']);
    rows.push(['']);
    rows.push(['']);

    // Routing Section
    rows.push(['ROUTING / PROCESS SEQUENCE']);
    rows.push(['Process / Operation', 'Step', 'Planned Qty', 'Completed Qty', 'Scrap Qty', 'Operator Name', 'Date Completed']);

    const sequence = order.process_sequence || [];
    sequence.forEach((step, idx) => {
      rows.push([step, idx + 1, order.quantity || '', '', '', '', '']);
    });

    // Add some empty rows for manual entries
    for (let i = 0; i < 3; i++) {
      rows.push(['', '', '', '', '', '', '']);
    }

    rows.push(['']);
    rows.push(['']);
    rows.push(['Issued By:', '', user?.email || '', '', 'Date:', '', new Date().toLocaleDateString()]);
    rows.push(['']);
    rows.push(['ISO 9001:2015 Controlled Document']);

    const ws = XLSX.utils.aoa_to_sheet(rows);

    // Set column widths
    ws['!cols'] = [
      { wch: 24 },  // A - Labels
      { wch: 4 },   // B - Spacer
      { wch: 28 },  // C - Values
      { wch: 12 },  // D - Planned Qty
      { wch: 14 },  // E - Completed Qty
      { wch: 10 },  // F - Scrap Qty
      { wch: 18 },  // G - Operator
      { wch: 15 }   // H - Date
    ];

    XLSX.utils.book_append_sheet(workbook, ws, 'Traveller');

    // Generate and download
    const fileName = `${order.order_number}_Traveller.xlsx`;
    XLSX.writeFile(workbook, fileName);
    toast.success(`Traveller downloaded: ${fileName}`);
  };

  return (
    <div className="min-h-screen bg-[#050505] relative overflow-hidden text-white">
      {/* Honeycomb Background */}
      <div className="fixed inset-0 opacity-30 pointer-events-none">
        <svg className="w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="honeycomb-cwo" x="0" y="0" width="56" height="100" patternUnits="userSpaceOnUse">
              <path d="M28 0L56 16.5V49.5L28 66L0 49.5V16.5L28 0Z" fill="none" stroke="rgba(34, 197, 94, 0.12)" strokeWidth="1"/>
              <path d="M28 66L56 82.5V115.5L28 132L0 115.5V82.5L28 66Z" fill="none" stroke="rgba(34, 197, 94, 0.12)" strokeWidth="1"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#honeycomb-cwo)" />
        </svg>
      </div>
      <div className="fixed inset-0 pointer-events-none" style={{ background: 'radial-gradient(ellipse at center, rgba(34, 197, 94, 0.06) 0%, transparent 70%)' }} />

      {/* Fixed Header */}
      <header className="fixed top-0 left-0 right-0 z-50 h-16 bg-slate-950/90 backdrop-blur-xl border-b border-green-500/20">
        <div className="h-full max-w-[1920px] mx-auto px-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/')} className="text-slate-400 hover:text-green-400 hover:bg-green-500/10" data-testid="back-btn">
              <Home className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                <ClipboardPlus className="w-5 h-5 text-green-400" />
              </div>
              <div>
                <h1 className="text-lg font-bold text-white uppercase tracking-wide" data-testid="page-title">Create Works Order</h1>
                <p className="text-xs text-slate-500">New Production Order</p>
              </div>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-2 text-sm">
            <span className="text-slate-500">Home</span>
            <ChevronRight className="w-4 h-4 text-slate-600" />
            <span className="text-green-400 font-medium">New Order</span>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 pt-20 px-4 sm:px-6 pb-8">
        <div className="max-w-[900px] mx-auto">
          {createdOrder ? (
            /* Success State — Order Created */
            <div className="bg-slate-900/40 backdrop-blur-md border border-green-500/20 rounded-xl p-8 text-center space-y-6" data-testid="order-created-success">
              <div className="w-20 h-20 mx-auto rounded-full bg-green-500/10 border border-green-500/30 flex items-center justify-center">
                <Check className="w-10 h-10 text-green-400" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white uppercase tracking-wide">Works Order Created</h2>
                <p className="text-green-400 font-mono text-xl font-bold mt-2" data-testid="created-order-number">{createdOrder.order_number}</p>
              </div>
              <div className="bg-slate-950/50 border border-slate-800 rounded-lg p-4 text-left max-w-md mx-auto">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div className="text-slate-500">Customer:</div><div className="text-white">{createdOrder.customer}</div>
                  <div className="text-slate-500">Part:</div><div className="text-white font-mono">{createdOrder.part_number}</div>
                  <div className="text-slate-500">Quantity:</div><div className="text-white font-bold">{createdOrder.quantity}</div>
                  <div className="text-slate-500">Due Date:</div><div className="text-white">{createdOrder.due_date}</div>
                  {createdOrder.customer_order_number && <><div className="text-slate-500">PO Number:</div><div className="text-white font-mono">{createdOrder.customer_order_number}</div></>}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-4">
                <Button
                  onClick={generateTraveller}
                  className="bg-green-600 hover:bg-green-700 text-white font-bold uppercase tracking-wide px-8 py-3 text-base"
                  data-testid="generate-traveller-btn"
                >
                  <FileSpreadsheet className="w-5 h-5 mr-2" />
                  Generate Excel Traveller
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/kanban')}
                  className="border-slate-700 text-slate-300 hover:bg-slate-800"
                  data-testid="go-to-kanban-btn"
                >
                  Go to Kanban Board
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => { setCreatedOrder(null); setFormData({ order_number: '', customer: '', customer_order_number: '', part_number: '', quantity: '', unit_price: '', due_date: '', notes: '', is_trial: false }); setSelectedPart(null); }}
                  className="text-slate-500 hover:text-white"
                  data-testid="create-another-btn"
                >
                  Create Another Order
                </Button>
              </div>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="bg-slate-900/40 backdrop-blur-md border border-green-500/10 rounded-xl p-8" data-testid="create-works-order-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              
              {/* Part Selection - Searchable */}
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Part Number *</Label>
                <Popover open={partSearchOpen} onOpenChange={setPartSearchOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      role="combobox"
                      aria-expanded={partSearchOpen}
                      data-testid="part-select"
                      className="w-full justify-between bg-slate-950/50 border-slate-800 font-mono text-left hover:bg-slate-900/50"
                    >
                      {formData.part_number ? (
                        <span className="truncate">
                          {formData.part_number} - {parts.find(p => p.part_number === formData.part_number)?.description || ''}
                        </span>
                      ) : (
                        <span className="text-slate-500">Search or select part...</span>
                      )}
                      <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-[600px] p-0 bg-slate-900 border-slate-700" align="start">
                    <Command className="bg-transparent">
                      <CommandInput 
                        placeholder="Search by part number or description..." 
                        className="border-none focus:ring-0"
                      />
                      <CommandList>
                        <CommandEmpty>No parts found.</CommandEmpty>
                        <CommandGroup className="max-h-[300px] overflow-y-auto">
                          {parts.map((part) => (
                            <CommandItem
                              key={part.part_number}
                              value={`${part.part_number} ${part.description}`}
                              onSelect={() => {
                                handlePartSelect(part.part_number);
                                setPartSearchOpen(false);
                              }}
                              className="cursor-pointer hover:bg-slate-800"
                            >
                              <Check
                                className={`mr-2 h-4 w-4 ${formData.part_number === part.part_number ? "opacity-100 text-green-400" : "opacity-0"}`}
                              />
                              <span className="font-mono text-green-400">{part.part_number}</span>
                              <span className="ml-2 text-slate-400 truncate">{part.description}</span>
                            </CommandItem>
                          ))}
                        </CommandGroup>
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              </div>

              {/* Selected Part Details */}
              {selectedPart && (
                <div className="md:col-span-2 bg-slate-900/60 border border-green-500/20 rounded-lg p-4" data-testid="part-details">
                  <p className="text-xs text-slate-500 uppercase tracking-wider mb-3">Part Details</p>
                  <div className="mb-3">
                    <span className="text-xs text-slate-500">Description:</span>
                    <p className="text-white text-sm">{selectedPart.description}</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm mb-3">
                    <div>
                      <span className="text-slate-500 text-xs">Drawing:</span>
                      <p className="font-mono text-white">{selectedPart.drawing_number}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Material:</span>
                      <p className="text-white">{selectedPart.material_grade}</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">Diameter:</span>
                      <p className="text-white">{selectedPart.diameter} mm</p>
                    </div>
                    <div>
                      <span className="text-slate-500 text-xs">LOC:</span>
                      <p className="text-white">{selectedPart.loc} mm</p>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-slate-800">
                    <div className="mb-3">
                      <p className="text-xs text-slate-500 mb-1">Pieces per Bar</p>
                      <p className="text-green-400 font-bold text-lg">
                        {Math.floor(5800 / (selectedPart.loc + 2))} pieces/bar
                      </p>
                    </div>
                    <p className="text-xs text-slate-500 mb-2">Process Sequence:</p>
                    <div className="flex flex-wrap gap-2">
                      {selectedPart.process_sequence.map((proc, idx) => (
                        <span key={idx} className="px-2 py-1 bg-slate-800 text-white text-xs font-mono rounded border border-slate-700">
                          {idx + 1}. {proc}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {/* Order Number */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Works Order Number (Optional)</Label>
                <Input
                  data-testid="order-number-input"
                  value={formData.order_number}
                  onChange={(e) => handleInputChange('order_number', e.target.value.toUpperCase())}
                  className="bg-slate-950/50 border-slate-800 font-mono"
                  placeholder="Leave blank to auto-generate"
                />
                <p className="text-xs text-slate-600 mt-1">Leave blank to auto-generate (e.g., WO-XXXXXXXX)</p>
              </div>

              {/* Customer */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Customer *</Label>
                <div className="flex gap-2">
                  <Select value={formData.customer} onValueChange={(value) => handleInputChange('customer', value)}>
                    <SelectTrigger data-testid="customer-select" className="bg-slate-950/50 border-slate-800 flex-1">
                      <SelectValue placeholder="Select customer" />
                    </SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.customer_id} value={customer.name}>
                          {customer.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddCustomer(true)}
                    className="border-green-500/30 text-green-400 hover:bg-green-500/10 px-3"
                    data-testid="add-customer-quick-btn"
                  >
                    <Plus className="w-4 h-4" />
                  </Button>
                </div>
                {customers.length === 0 && (
                  <p className="text-xs text-amber-400 mt-1">No customers found. Click + to add one.</p>
                )}
              </div>

              {/* Customer Order Number */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Customer Order Number</Label>
                <Input
                  data-testid="customer-order-number-input"
                  value={formData.customer_order_number}
                  onChange={(e) => handleInputChange('customer_order_number', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="PO-12345"
                />
              </div>

              {/* Quantity */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Quantity *</Label>
                <Input
                  type="number"
                  data-testid="quantity-input"
                  value={formData.quantity}
                  onChange={(e) => handleInputChange('quantity', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="100"
                  min="1"
                  required
                />
                {formData.quantity && selectedPart && barsNeeded > 0 && (
                  <div className="mt-2 p-3 bg-green-500/10 border border-green-500/30 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Material Required</p>
                    <p className="text-green-400 font-bold text-lg">
                      {barsNeeded} bars needed
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      ({formData.quantity} pcs ÷ {Math.floor(5800 / (selectedPart.loc + 2))} pcs/bar = {(parseInt(formData.quantity) / Math.floor(5800 / (selectedPart.loc + 2))).toFixed(2)})
                    </p>
                  </div>
                )}
              </div>

              {/* Unit Price */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                  <DollarSign className="w-3 h-3" />
                  Unit Price (ZAR) *
                </Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-bold">R</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    data-testid="unit-price-input"
                    value={formData.unit_price}
                    onChange={(e) => handleInputChange('unit_price', e.target.value)}
                    className="bg-slate-950/50 border-slate-800 pl-8"
                    placeholder="0.00"
                    required
                  />
                </div>
                {formData.quantity && formData.unit_price && (
                  <div className="mt-2 p-3 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
                    <p className="text-xs text-slate-500 uppercase tracking-wider mb-1">Order Value</p>
                    <p className="text-emerald-400 font-bold text-lg">
                      R {(parseInt(formData.quantity) * parseFloat(formData.unit_price)).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                )}
              </div>

              {/* Due Date */}
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Due Date *</Label>
                <Input
                  type="date"
                  data-testid="due-date-input"
                  value={formData.due_date}
                  onChange={(e) => handleInputChange('due_date', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  required
                />
              </div>

              {/* Trial / First-Off */}
              <div className="md:col-span-2">
                <label
                  className="flex items-center gap-3 cursor-pointer group p-4 rounded-lg border border-slate-800 hover:border-red-500/40 transition-colors"
                  data-testid="trial-checkbox-label"
                >
                  <input
                    type="checkbox"
                    checked={formData.is_trial}
                    onChange={(e) => handleInputChange('is_trial', e.target.checked)}
                    className="w-5 h-5 rounded border-slate-600 bg-slate-950 text-red-500 focus:ring-red-500/50 cursor-pointer"
                    data-testid="trial-checkbox"
                  />
                  <div>
                    <span className="text-sm font-bold text-white uppercase tracking-wide group-hover:text-red-400 transition-colors">Trial / First-Off Order</span>
                    <p className="text-xs text-slate-500 mt-0.5">Mark this order as a trial run for visual tracking on the Kanban board</p>
                  </div>
                </label>
              </div>

              {/* Notes */}
              <div className="md:col-span-2">
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">Notes</Label>
                <Textarea
                  data-testid="notes-input"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  className="bg-slate-950/50 border-slate-800"
                  placeholder="Optional notes..."
                  rows={3}
                />
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="mt-8 flex gap-4 pt-6 border-t border-slate-800">
              <Button
                type="submit"
                disabled={submitting}
                data-testid="submit-btn"
                className="bg-green-500/20 hover:bg-green-500/30 text-green-400 border border-green-500/50 hover:shadow-[0_0_20px_rgba(34,197,94,0.3)] font-bold uppercase tracking-wide"
              >
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <ClipboardPlus className="w-4 h-4 mr-2" />}
                {submitting ? 'Creating...' : 'Create Works Order'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/')}
                data-testid="cancel-btn"
                className="border-slate-700 hover:border-slate-600"
              >
                Cancel
              </Button>
            </div>
          </form>
          )}
        </div>
      </main>

      {/* Quick Add Customer Dialog */}
      <Dialog open={showAddCustomer} onOpenChange={setShowAddCustomer}>
        <DialogContent className="bg-slate-900 border-green-500/30 max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Quick Add Customer
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Customer Name *</Label>
              <Input
                value={newCustomerName}
                onChange={(e) => setNewCustomerName(e.target.value)}
                className="bg-slate-800 border-slate-700"
                placeholder="Company Name"
                data-testid="quick-customer-name-input"
                onKeyDown={(e) => e.key === 'Enter' && handleQuickAddCustomer()}
              />
              <p className="text-xs text-slate-500 mt-1">
                You can add more details later in Customer Management
              </p>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddCustomer(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button 
                onClick={handleQuickAddCustomer} 
                disabled={addingCustomer} 
                className="bg-green-600 hover:bg-green-500"
                data-testid="quick-add-customer-submit"
              >
                {addingCustomer ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Customer
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreateWorksOrder;
